import { EditorView } from '@codemirror/view';
import { syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import {
  autocompletion,
  type CompletionContext,
  type Completion,
} from '@codemirror/autocomplete';
import type { Extension } from '@codemirror/state';

const TERRA_ROOT_COMPLETIONS: Completion[] = [
  {
    label: 'terra.request',
    type: 'variable',
    detail: 'Mutable request object',
  },
  {
    label: 'terra.variables',
    type: 'variable',
    detail: 'Persisted key-value store',
  },
  {
    label: 'terra.credentials',
    type: 'variable',
    detail: 'Active credential data (read-only)',
  },
  {
    label: 'terra.helpers',
    type: 'variable',
    detail: 'Crypto & utility helpers',
  },
];

const TERRA_REQUEST_COMPLETIONS: Completion[] = [
  {
    label: 'terra.request.method',
    type: 'property',
    detail: 'string',
    info: 'HTTP method (GET, POST, etc.)',
  },
  {
    label: 'terra.request.url',
    type: 'property',
    detail: 'string',
    info: 'Full request URL',
  },
  {
    label: 'terra.request.headers',
    type: 'property',
    detail: 'Record<string, string | string[]>',
    info: 'Request headers object',
  },
  {
    label: 'terra.request.body',
    type: 'property',
    detail: 'string',
    info: 'Raw request body',
  },
];

const TERRA_VARIABLES_COMPLETIONS: Completion[] = [
  {
    label: 'terra.variables.get',
    type: 'method',
    detail: '(key: string) => string | undefined',
    info: 'Get a variable value',
  },
  {
    label: 'terra.variables.set',
    type: 'method',
    detail: '(key: string, value: string) => void',
    info: 'Set a variable value',
  },
  {
    label: 'terra.variables.has',
    type: 'method',
    detail: '(key: string) => boolean',
    info: 'Check if a variable exists',
  },
  {
    label: 'terra.variables.unset',
    type: 'method',
    detail: '(key: string) => void',
    info: 'Remove a variable',
  },
  {
    label: 'terra.variables.toObject',
    type: 'method',
    detail: '() => Record<string, string>',
    info: 'Get all variables as an object',
  },
  {
    label: 'terra.variables.clear',
    type: 'method',
    detail: '() => void',
    info: 'Remove all variables',
  },
];

const TERRA_CREDENTIALS_COMPLETIONS: Completion[] = [
  {
    label: 'terra.credentials.username',
    type: 'property',
    detail: 'string',
    info: 'Credential username',
  },
  {
    label: 'terra.credentials.password',
    type: 'property',
    detail: 'string',
    info: 'Credential password',
  },
  {
    label: 'terra.credentials.role',
    type: 'property',
    detail: 'string',
    info: 'Credential role (e.g., USER, ADMIN)',
  },
  {
    label: 'terra.credentials.secrets',
    type: 'variable',
    detail: '{ get, has, toObject }',
    info: 'Custom key-value secrets (e.g., apiKey, apiSecret)',
  },
  {
    label: 'terra.credentials.headers',
    type: 'variable',
    detail: '{ get, has, toObject }',
    info: 'Credential HTTP headers',
  },
  {
    label: 'terra.credentials.cookies',
    type: 'variable',
    detail: '{ get, has, toObject }',
    info: 'Credential cookies',
  },
];

const TERRA_CREDENTIALS_SUB_COMPLETIONS: Completion[] = [
  {
    label: 'get',
    type: 'method',
    detail: '(key: string) => string | undefined',
    info: 'Get a value by key',
  },
  {
    label: 'has',
    type: 'method',
    detail: '(key: string) => boolean',
    info: 'Check if a key exists',
  },
  {
    label: 'toObject',
    type: 'method',
    detail: '() => Record<string, string>',
    info: 'Get all entries as an object',
  },
];

export type CredentialKeyHints = {
  secretKeys: string[];
  headerKeys: string[];
  cookieNames: string[];
};

const TERRA_HELPERS_COMPLETIONS: Completion[] = [
  {
    label: 'terra.helpers.hmacSha256',
    type: 'function',
    detail: '(data, secret) => string',
    info: 'HMAC-SHA256 hex digest',
  },
  {
    label: 'terra.helpers.hmacSha512',
    type: 'function',
    detail: '(data, secret) => string',
    info: 'HMAC-SHA512 hex digest',
  },
  {
    label: 'terra.helpers.sha256',
    type: 'function',
    detail: '(data) => string',
    info: 'SHA-256 hex digest',
  },
  {
    label: 'terra.helpers.sha512',
    type: 'function',
    detail: '(data) => string',
    info: 'SHA-512 hex digest',
  },
  {
    label: 'terra.helpers.md5',
    type: 'function',
    detail: '(data) => string',
    info: 'MD5 hex digest',
  },
  {
    label: 'terra.helpers.base64Encode',
    type: 'function',
    detail: '(data) => string',
    info: 'Base64 encode',
  },
  {
    label: 'terra.helpers.base64Decode',
    type: 'function',
    detail: '(data) => string',
    info: 'Base64 decode',
  },
  {
    label: 'terra.helpers.uuid',
    type: 'function',
    detail: '() => string',
    info: 'Random UUID v4',
  },
  {
    label: 'terra.helpers.timestamp',
    type: 'function',
    detail: '() => number',
    info: 'Unix timestamp (seconds)',
  },
  {
    label: 'terra.helpers.timestampMs',
    type: 'function',
    detail: '() => number',
    info: 'Unix timestamp (milliseconds)',
  },
  {
    label: 'terra.helpers.randomBytes',
    type: 'function',
    detail: '(length) => string',
    info: 'Random hex string',
  },
  {
    label: 'terra.helpers.randomInt',
    type: 'function',
    detail: '(min, max) => number',
    info: 'Random integer in [min, max)',
  },
];

function buildKeyCompletions({
  prefix,
  keys,
  category,
}: {
  prefix: string;
  keys: string[];
  category: string;
}): Completion[] {
  return keys.map((key) => ({
    label: `${prefix}get(${JSON.stringify(key)})`,
    type: 'method',
    detail: `→ ${category}`,
    info: `Get ${category} ${JSON.stringify(key)}`,
    boost: 1,
  }));
}

function buildCredentialSubOptions({
  prefix,
  keys,
  category,
}: {
  prefix: string;
  keys: string[];
  category: string;
}): Completion[] {
  const generic = TERRA_CREDENTIALS_SUB_COMPLETIONS.map((c) => ({
    ...c,
    label: prefix + c.label,
  }));
  return [...buildKeyCompletions({ prefix, keys, category }), ...generic];
}

function createTerraCompletionSource(hints?: CredentialKeyHints) {
  return function terraCompletionSource(context: CompletionContext) {
    const word = context.matchBefore(/terra(\.\w*)*\.?\w*/);
    if (!word) return null;
    if (word.from === word.to && !context.explicit) return null;

    const text = word.text;
    let options: Completion[];

    if (text.startsWith('terra.request.') || text === 'terra.request') {
      options = TERRA_REQUEST_COMPLETIONS;
    } else if (
      text.startsWith('terra.variables.') ||
      text === 'terra.variables'
    ) {
      options = TERRA_VARIABLES_COMPLETIONS;
    } else if (text.startsWith('terra.credentials.secrets.')) {
      options = buildCredentialSubOptions({
        prefix: 'terra.credentials.secrets.',
        keys: hints?.secretKeys ?? [],
        category: 'secret',
      });
    } else if (text.startsWith('terra.credentials.headers.')) {
      options = buildCredentialSubOptions({
        prefix: 'terra.credentials.headers.',
        keys: hints?.headerKeys ?? [],
        category: 'header',
      });
    } else if (text.startsWith('terra.credentials.cookies.')) {
      options = buildCredentialSubOptions({
        prefix: 'terra.credentials.cookies.',
        keys: hints?.cookieNames ?? [],
        category: 'cookie',
      });
    } else if (
      text.startsWith('terra.credentials.') ||
      text === 'terra.credentials'
    ) {
      options = TERRA_CREDENTIALS_COMPLETIONS;
    } else if (text.startsWith('terra.helpers.') || text === 'terra.helpers') {
      options = TERRA_HELPERS_COMPLETIONS;
    } else {
      options = TERRA_ROOT_COMPLETIONS;
    }

    return { from: word.from, options, validFor: /^[\w.("]*$/ };
  };
}

export function createTerraAutocomplete(hints?: CredentialKeyHints) {
  return autocompletion({
    override: [createTerraCompletionSource(hints)],
    activateOnTyping: true,
    defaultKeymap: true,
  });
}

const SCRIPT_COLORS_LIGHT = {
  keyword: '#d73a49',
  string: '#032f62',
  number: '#005cc5',
  property: '#6f42c1',
  boolean: '#e36209',
  comment: '#6a737d',
  punctuation: '#6a737d',
  foreground: '#24292e',
  background: '#fff',
  selection: '#BBDFFF',
  gutterBackground: '#fff',
  gutterForeground: '#6e7781',
  lineHighlight: '#f6f8fa',
} as const;

const SCRIPT_COLORS_DARK = {
  keyword: '#ff7b72',
  string: '#a5d6ff',
  number: '#79c0ff',
  property: '#d2a8ff',
  boolean: '#ffab70',
  comment: '#8b949e',
  punctuation: '#8b949e',
  foreground: '#c9d1d9',
  background: '#0d1117',
  selection: '#003d73',
  gutterBackground: '#0d1117',
  gutterForeground: '#8b949e',
  lineHighlight: '#36334280',
} as const;

type ScriptColorPalette = {
  readonly keyword: string;
  readonly string: string;
  readonly number: string;
  readonly property: string;
  readonly boolean: string;
  readonly comment: string;
  readonly punctuation: string;
  readonly foreground: string;
  readonly background: string;
  readonly selection: string;
  readonly gutterBackground: string;
  readonly gutterForeground: string;
  readonly lineHighlight: string;
};

function createScriptEditorTheme(
  colors: ScriptColorPalette,
  isDark: boolean,
): Extension {
  const editorTheme = EditorView.theme(
    {
      '&': {
        fontSize: '12px',
        flex: '1',
        minHeight: '0',
        backgroundColor: colors.background,
        color: colors.foreground,
      },
      '.cm-content': {
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
        padding: '8px 0',
      },
      '.cm-scroller': {
        backgroundColor: colors.background,
      },
      '.cm-gutters': {
        border: 'none',
        backgroundColor: colors.gutterBackground,
        color: colors.gutterForeground,
      },
      '.cm-line': {
        padding: '0 8px',
      },
      '.cm-activeLineGutter': {
        backgroundColor: colors.lineHighlight,
      },
      '.cm-activeLine': {
        backgroundColor: colors.lineHighlight,
      },
      '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
        {
          backgroundColor: colors.selection,
        },
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: colors.foreground,
      },
      '&.cm-focused': {
        outline: 'none',
      },
    },
    { dark: isDark },
  );

  const highlightStyle = HighlightStyle.define([
    { tag: tags.keyword, color: colors.keyword, fontWeight: '700' },
    { tag: tags.controlKeyword, color: colors.keyword, fontWeight: '700' },
    { tag: [tags.name, tags.variableName], color: colors.number },
    { tag: tags.propertyName, color: colors.property, fontWeight: '500' },
    { tag: tags.typeName, color: colors.keyword },
    { tag: tags.className, color: colors.property },
    { tag: tags.number, color: colors.number },
    { tag: [tags.string, tags.regexp], color: colors.string },
    {
      tag: [tags.comment, tags.lineComment],
      color: colors.comment,
      fontStyle: 'italic',
    },
    { tag: [tags.bool, tags.null, tags.atom], color: colors.boolean },
    {
      tag: [tags.operator, tags.punctuation],
      color: colors.punctuation,
    },
    {
      tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
      color: colors.property,
    },
    {
      tag: tags.definition(tags.variableName),
      color: colors.property,
    },
  ]);

  return [editorTheme, syntaxHighlighting(highlightStyle)];
}

const scriptEditorLightTheme = createScriptEditorTheme(
  SCRIPT_COLORS_LIGHT,
  false,
);
const scriptEditorDarkTheme = createScriptEditorTheme(SCRIPT_COLORS_DARK, true);

export function getTerraEditorThemeByMode({
  isDark,
}: {
  isDark: boolean;
}): Extension {
  return isDark ? scriptEditorDarkTheme : scriptEditorLightTheme;
}
